import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  FilePlus2,
  FileText,
  Sparkles,
  Trash2,
} from 'lucide-react';
import ContractsTable, {
  type ContractsTableColumn,
} from '@/modules/org/contracts/components/contracts-table';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { TablePagination, type PaginationState } from '@/components/shared/TablePagination';
import { cn } from '@/lib/utils';
import {
  useGetContractAnalyses,
  useDeleteContractAnalysis,
} from '../hooks/use-contract-analyses';
import type { OrgContractAnalysisItem } from '../types/contract-analysis.types';

const PAGE_SIZE = 10;

interface ContractAnalysesListProps {
  /** Switched org id (`useCurrentOrgId`). */
  orgId: string;
  /** URL slug for nested links (`/org/<slug>/contract-checker/...`). */
  slug: string;
}

/**
 * Body of the Contract Checker list page. Renders the desktop table
 * via the shared `<ContractsTable>` and a stacked card view on
 * mobile. Owns its own pagination state.
 */
export function ContractAnalysesList({ orgId, slug }: ContractAnalysesListProps) {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const deleteMutation = useDeleteContractAnalysis();

  const queryParams = useMemo(
    () => ({
      paging: { pageNo: currentPage, pageSize: PAGE_SIZE },
      where: {
        OrganizationId: { eq: orgId },
        or: [{ IsInTrash: { eq: false } }, { IsInTrash: { eq: null } }],
      },
    }),
    [orgId, currentPage]
  );

  const { data, isLoading } = useGetContractAnalyses(queryParams);

  const rows = data?.items ?? [];
  const total = data?.totalCount ?? 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startItem = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const endItem = total === 0 ? 0 : Math.min(safePage * PAGE_SIZE, total);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
    [i18n.language]
  );

  const columns = useMemo<ContractsTableColumn<OrgContractAnalysisItem>[]>(
    () => [
      {
        id: 'projectName',
        header: t('ORGCONTRACTCHECKER_LIST_COL_NAME', { defaultValue: 'Project Name' }),
        cell: (row) => (
          <Link
            to={`/org/${slug}/contract-checker/result/${row.ItemId}`}
            className="block max-w-90 truncate text-[15px] font-semibold leading-5 text-[hsl(var(--fg))] hover:text-selise-blue"
          >
            {row.ProjectName || t('ORGCONTRACTCHECKER_LIST_UNTITLED', { defaultValue: 'Untitled' })}
          </Link>
        ),
      },
      {
        id: 'source',
        header: t('ORGCONTRACTCHECKER_LIST_COL_SOURCE', { defaultValue: 'Source' }),
        cell: (row) => <SourceCell row={row} />,
      },
      {
        id: 'risk',
        header: t('ORGCONTRACTCHECKER_LIST_COL_RISK', { defaultValue: 'Risk Summary' }),
        cell: (row) => <RiskSummaryCell row={row} />,
      },
      {
        id: 'created',
        header: t('ORGCONTRACTCHECKER_LIST_COL_DATE', { defaultValue: 'Created' }),
        cell: (row) => (
          <p className="text-[15px] font-semibold text-[hsl(var(--fg))]">
            {row.CreatedDate ? dateFormatter.format(new Date(row.CreatedDate)) : '-'}
          </p>
        ),
      },
      {
        id: 'action',
        header: t('ORGCONTRACTS_COLUMNS_ACTION', { defaultValue: 'Action' }),
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (row) => (
          <RowActions
            slug={slug}
            itemId={row.ItemId}
            isDeleting={deleteMutation.isPending}
            onDelete={() => deleteMutation.mutate(row.ItemId)}
          />
        ),
      },
    ],
    [t, dateFormatter, slug, deleteMutation]
  );

  const showingLabel =
    total > 0
      ? t('ORGCONTRACTCHECKER_LIST_SHOWING', {
          defaultValue: 'Showing {{start}}-{{end}} of {{total}}',
          start: startItem,
          end: endItem,
          total,
        })
      : undefined;

  if (!isLoading && rows.length === 0) {
    return <EmptyState slug={slug} />;
  }

  const pagination: PaginationState = {
    page: safePage,
    limit: PAGE_SIZE,
    total,
    totalPages,
  };

  return (
    <>
      {/* Desktop: data table */}
      <div className="hidden md:block">
        <ContractsTable<OrgContractAnalysisItem>
          columns={columns}
          data={rows}
          rowKey={(row) => row.ItemId}
          isLoading={isLoading}
          pagination={pagination}
          onPageChange={setCurrentPage}
          showingLabel={showingLabel}
        />
      </div>

      {/* Mobile: stacked card view */}
      <div className="md:hidden">
        <CardView
          rows={rows}
          slug={slug}
          isLoading={isLoading}
          dateFormatter={dateFormatter}
          deleteMutation={deleteMutation}
          pagination={pagination}
          onPageChange={setCurrentPage}
          showingLabel={showingLabel}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Mobile card view — same data as the table, denser layout
// ---------------------------------------------------------------------------

interface CardViewProps {
  rows: OrgContractAnalysisItem[];
  slug: string;
  isLoading: boolean;
  dateFormatter: Intl.DateTimeFormat;
  deleteMutation: ReturnType<typeof useDeleteContractAnalysis>;
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  showingLabel?: string;
}

function CardView({
  rows,
  slug,
  isLoading,
  dateFormatter,
  deleteMutation,
  pagination,
  onPageChange,
  showingLabel,
}: CardViewProps) {
  const { t } = useTranslation();
  const skeletonRows = Math.max(pagination.limit ?? 5, 1);

  return (
    <div className={cn('overflow-hidden rounded-xl border border-border bg-background')}>
      <div className="flex flex-col gap-3 p-3">
        {isLoading ? (
          Array.from({ length: skeletonRows }).map((_, i) => (
            <div
              key={`contract-analyses-card-skeleton-${i}`}
              className="rounded-lg border border-border bg-background p-4 space-y-3"
            >
              <Skeleton className="h-4 w-3/4 rounded-full" />
              <Skeleton className="h-3 w-1/2 rounded-full" />
              <Skeleton className="h-3 w-2/3 rounded-full" />
            </div>
          ))
        ) : (
          rows.map((row) => (
            <article
              key={row.ItemId}
              className="rounded-lg border border-border bg-background p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <Link
                  to={`/org/${slug}/contract-checker/result/${row.ItemId}`}
                  className="min-w-0 flex-1 space-y-1 cursor-pointer"
                >
                  <p className="text-[15px] font-semibold leading-5 text-[hsl(var(--fg))] line-clamp-2 hover:text-selise-blue">
                    {row.ProjectName ||
                      t('ORGCONTRACTCHECKER_LIST_UNTITLED', { defaultValue: 'Untitled' })}
                  </p>
                  <SourceCell row={row} />
                </Link>
                <div className="shrink-0">
                  <RowActions
                    slug={slug}
                    itemId={row.ItemId}
                    isDeleting={deleteMutation.isPending}
                    onDelete={() => deleteMutation.mutate(row.ItemId)}
                  />
                </div>
              </div>

              <div className="flex items-end justify-between gap-3">
                <div className="min-w-0 space-y-0.5">
                  <p className="text-xs text-globe-grey">
                    {t('ORGCONTRACTCHECKER_LIST_COL_DATE', { defaultValue: 'Created' })}
                  </p>
                  <p className="text-sm font-semibold text-[hsl(var(--fg))]">
                    {row.CreatedDate ? dateFormatter.format(new Date(row.CreatedDate)) : '-'}
                  </p>
                </div>
                <RiskSummaryCell row={row} />
              </div>
            </article>
          ))
        )}
      </div>

      {!isLoading && pagination.total > 0 ? (
        <div className="border-t border-border bg-background px-3 py-2">
          <TablePagination
            pagination={pagination}
            onPageChange={onPageChange}
            showingLabel={showingLabel}
          />
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cell sub-components (shared between table and card view)
// ---------------------------------------------------------------------------

function SourceCell({ row }: { row: OrgContractAnalysisItem }) {
  const { t } = useTranslation();
  if (row.SourceContractTitle) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Sparkles className="w-4 h-4 text-selise-blue shrink-0" />
        <span className="truncate max-w-60 text-[hsl(var(--fg))]">
          {t('ORGCONTRACTCHECKER_LIST_SOURCE_FROMCONTRACT', {
            defaultValue: 'From: {{title}}',
            title: row.SourceContractTitle,
          })}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm text-globe-grey">
      <FileText className="w-4 h-4 shrink-0" />
      <span className="truncate max-w-60">
        {row.SourceFileName ??
          t('ORGCONTRACTCHECKER_LIST_SOURCE_UPLOADED', { defaultValue: 'Uploaded file' })}
      </span>
    </div>
  );
}

function RiskSummaryCell({ row }: { row: OrgContractAnalysisItem }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {row.HighRiskCount > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-destructive/10 text-destructive text-xs font-semibold">
          <AlertTriangle className="w-3 h-3" />
          {row.HighRiskCount}
        </span>
      )}
      {row.MediumRiskCount > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[hsl(var(--warning))/0.15] text-[hsl(var(--warning))] text-xs font-semibold">
          <AlertCircle className="w-3 h-3" />
          {row.MediumRiskCount}
        </span>
      )}
      {row.HighRiskCount === 0 && row.MediumRiskCount === 0 && (
        <span className="text-xs text-globe-grey">
          {row.TotalPoints} point{row.TotalPoints === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}

interface RowActionsProps {
  slug: string;
  itemId: string;
  isDeleting: boolean;
  onDelete: () => void;
}

function RowActions({ slug, itemId, isDeleting, onDelete }: RowActionsProps) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-input bg-background">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="h-9 rounded-none border-0 px-5 text-[hsl(var(--fg))]"
        disabled={isDeleting}
      >
        <Link to={`/org/${slug}/contract-checker/result/${itemId}`}>
          {t('ORGCONTRACTS_ACTION_VIEW', { defaultValue: 'View' })}
        </Link>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 rounded-none border-l border-input px-2"
            disabled={isDeleting}
          >
            <ChevronDown className="size-4" />
            <span className="sr-only">
              {t('ORGCONTRACTS_ACTION_OPENACTIONS', { defaultValue: 'Open actions' })}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem
            variant="destructive"
            onSelect={(event) => {
              if (isDeleting) {
                event.preventDefault();
                return;
              }
              onDelete();
            }}
            disabled={isDeleting}
          >
            <Trash2 className="size-4" />
            {t('ORGCONTRACTS_ACTION_DELETE', { defaultValue: 'Delete' })}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-dashed border-border bg-card/50 p-10 flex flex-col items-center justify-center text-center">
      <div className="w-14 h-14 rounded-full bg-selise-blue/10 flex items-center justify-center mb-4">
        <FilePlus2 className="w-7 h-7 text-selise-blue" />
      </div>
      <h2 className="text-lg font-semibold text-[hsl(var(--fg))] font-heading">
        {t('ORGCONTRACTCHECKER_LIST_EMPTY_TITLE', { defaultValue: 'No analyses yet' })}
      </h2>
      <p className="text-sm text-globe-grey mt-1 max-w-md">
        {t('ORGCONTRACTCHECKER_LIST_EMPTY_DESC', {
          defaultValue: 'Upload a contract or analyze one from your library.',
        })}
      </p>
      <Button
        type="button"
        variant="primary"
        className="mt-5"
        onClick={() => navigate(`/org/${slug}/contract-checker/new`)}
      >
        {t('ORGCONTRACTCHECKER_LIST_NEW', { defaultValue: 'New Analysis' })}
      </Button>
    </div>
  );
}

export default ContractAnalysesList;
